import { apiClient } from '../client';

export interface PetpoojaSummary {
  restaurants: number;
  categories: number;
  items: number;
  modifiers: number;
  inStockItems: number;
  outOfStockItems: number;
  activeItems: number;
  inactiveItems: number;
  lastSyncAt: string | null;
  storeStatus?: 'open' | 'closed' | null;
  storeRestaurantId?: string | null;
  storeRestaurantName?: string | null;
  storeStatusUpdatedAt?: string | null;
  config?: {
    configured: boolean;
  };
}

export interface PetpoojaRestaurant {
  id: string;
  restaurant_id: string;
  name: string;
  active: boolean;
  updated_at: string;
}

export interface PetpoojaCategory {
  id: string;
  restaurant_id: string;
  category_id: string;
  parent_category_id: string | null;
  name: string;
  category_rank: number;
  active: boolean;
}

export interface PetpoojaItem {
  id: string;
  restaurant_id: string;
  item_id: string;
  category_id: string;
  name: string;
  description?: string | null;
  price: string;
  item_type?: string | null;
  image_url?: string | null;
  in_stock: boolean;
  has_addons?: boolean;
  active: boolean;
  updated_at: string;
  catalog_sync_status?: 'synced' | 'skipped' | 'failed' | null;
  catalog_sync_error?: string | null;
}

export interface PetpoojaModifier {
  id: string;
  restaurant_id: string;
  item_id: string;
  modifier_id: string;
  name: string;
  price: string;
  active: boolean;
}

export interface PetpoojaOrder {
  id: string;
  order_number: string;
  total_amount: number;
  currency: string;
  status: string;
  payment_status: string;
  payment_method?: string | null;
  // Customer-facing notes string written at submit time by
  // handlePetpoojaSubmitOrder. Carries the table-source label resolved from
  // the keyword stamp ({{contact.custom_fields.last_trigger_keyword}}) so
  // restaurant staff can see which table an order came from in the
  // /home/petpooja/orders detail modal.
  notes?: string | null;
  created_at: string;
  updated_at: string;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  items_count?: number;
  meta_data?: {
    petpooja?: {
      rest_id?: string;
      client_order_id?: string;
      petpooja_order_id?: string | null;
      last_push_status?: string | null;
      last_push_at?: string | null;
      callback_url?: string | null;
    };
    [key: string]: any;
  };
}

export interface PetpoojaConnection {
  id: string;
  restaurant_id: string;
  menusharingcode?: string | null;
  restaurant_name?: string | null;
  base_url?: string;
  is_active: boolean;
  connected_at: string | null;
  disconnected_at: string | null;
  last_synced_at: string | null;
}

export interface PetpoojaConnectionStatus {
  connected: boolean;
  connections: PetpoojaConnection[];
  notification_settings?: Record<string, boolean>;
}

export interface PetpoojaConnectPayload {
  businessAccountId: string;
  restaurantId: string;
  menusharingcode: string; // Petpooja's term — required (deterministic key for save_order + webhook auth)
  restaurantName?: string;
  appKey: string;
  appSecret: string;
  accessToken: string;
  baseUrl?: string;
  fetchMenuUrl?: string;
}

export interface PetpoojaAddonItem {
  id: string;
  restaurant_id: string;
  addon_group_id: string;
  addon_item_id: string;
  name: string;
  price: string;
  rank: number;
  active: boolean;
}

export interface PetpoojaAddonGroup {
  id: string;
  restaurant_id: string;
  addon_group_id: string;
  name: string;
  rank: number;
  active: boolean;
  items: PetpoojaAddonItem[];
}

export interface PetpoojaDiscount {
  id: string;
  restaurant_id: string;
  discount_id: string;
  name: string;
  discount_type: 'P' | 'F'; // P=percentage, F=fixed
  discount_value: string;    // decimal
  order_types: string | null;
  applicable_on: string | null;
  has_coupon: boolean;
  coupon_code: string | null;
  min_amount: string | null;
  max_amount: string | null;
  time_from: string | null;
  time_to: string | null;
  discount_days: string | null;
  discount_on_total: boolean;
  active: boolean;
}

// PR-2: Multi-brand POS support — one Petpooja restaurantid hosts 1..N brands
// (each its own legal entity / GSTIN). See repository's listSiblingBrands +
// listGroupCategories docstrings for the data model.

/** Per-restaurant brand entry from /petpooja/group-categories */
export interface PetpoojaGroupCategory {
  id: string;
  groupCategoryId: string;
  name: string;          // raw Petpooja value, e.g. "Raju gari - 36AEMPV9871J1ZF"
  displayName: string;   // GST-suffix-stripped, e.g. "Raju gari" (falls back to name)
  gstNumber: string | null;
  rank: number;
  active: boolean;
  categoryCount: number;
  itemCount: number;
  outOfStockCount: number;
}

/** One brand entry in the sibling-brands aggregation response */
export interface PetpoojaSiblingBrand {
  groupCategoryId: string;
  name: string;
  displayName: string;
  gstNumber: string | null;
  rank: number;
  active: boolean;
  connectionStatus: 'connected' | 'not_connected';
  // Populated only when connectionStatus === 'connected'
  connectionId: string | null;
  restaurantId: string | null;
  restaurantName: string | null;
  menusharingcode: string | null;
  categoryCount: number;
  itemCount: number;
  outOfStockCount: number;
  // True for synthesised entries from pre-0159 single-brand pushmenus that
  // have categories but no group_categories rows. UI can render these with
  // a slightly different label.
  isLegacy: boolean;
}

/** One owner-group in the sibling-brands response */
export interface PetpoojaSiblingBrandGroup {
  restowner: string | null;       // null for legacy pre-0159 rows
  ownerLabel: string;             // "Petpooja Owner 257593" or "Unknown owner"
  connectedCount: number;
  totalBrandCount: number;
  brands: PetpoojaSiblingBrand[];
}

/** Top-level shape returned by /petpooja/sibling-brands */
export interface PetpoojaSiblingBrandsResponse {
  groups: PetpoojaSiblingBrandGroup[];
}

export const petpoojaApi = {
  // Connection management
  connect: (payload: PetpoojaConnectPayload) =>
    apiClient.post('/petpooja/connect', payload),
  disconnect: (payload: { businessAccountId: string; restaurantId: string }) =>
    apiClient.post('/petpooja/disconnect', payload),
  getConnectionStatus: (params: { businessAccountId: string }) =>
    apiClient.get<PetpoojaConnectionStatus>('/petpooja/connection-status', { params }),

  getSummary: (params?: { businessAccountId?: string }) =>
    apiClient.get<PetpoojaSummary>('/petpooja/summary', { params }),
  getRestaurants: (params?: { search?: string; limit?: number; offset?: number; businessAccountId?: string }) =>
    apiClient.get<PetpoojaRestaurant[]>('/petpooja/restaurants', { params }),
  getCategories: (params?: { restaurantId?: string; businessAccountId?: string; search?: string; groupCategoryId?: string; limit?: number; offset?: number }) =>
    apiClient.get<PetpoojaCategory[]>('/petpooja/categories', { params }),
  getItems: (params?: {
    restaurantId?: string;
    // Multi-tenant scoping fix: when no restaurantId, backend uses this to
    // restrict to the BA's owned restaurants. Required to prevent stale
    // cross-tenant rows leaking through (real bug observed with staging-era
    // addons appearing on a prod-connected BA).
    businessAccountId?: string;
    categoryId?: string;
    search?: string;
    inStock?: boolean;
    active?: boolean;
    hasAddons?: boolean;
    // Food type filter. Values: 'veg' | 'non_veg' | 'egg'. Resolved via
    // petpooja_attributes (attribute_id ↔ item_type join). Omit for all types.
    foodType?: string;
    // PR-2 multi-brand filter. Special values:
    //   - "uncategorized" → orphan bucket (group_category_id IS NULL or "0")
    //   - any other non-empty string → exact match on group_category_id
    groupCategoryId?: string;
    limit?: number;
    offset?: number;
  }) => apiClient.get<PetpoojaItem[]>('/petpooja/items', { params }),
  // PR-2: Per-restaurant brand picker source
  getGroupCategories: (params: { restaurantId: string }) =>
    apiClient.get<PetpoojaGroupCategory[]>('/petpooja/group-categories', { params }),
  // PR-2: Cross-connection sibling-brand aggregation grouped by restowner
  getSiblingBrands: (params: { businessAccountId: string }) =>
    apiClient.get<PetpoojaSiblingBrandsResponse>('/petpooja/sibling-brands', { params }),
  getModifiers: (params?: { restaurantId?: string; businessAccountId?: string; itemId?: string; search?: string; groupCategoryId?: string; limit?: number; offset?: number }) =>
    apiClient.get<PetpoojaModifier[]>('/petpooja/modifiers', { params }),
  getVariations: (params?: { restaurantId?: string; businessAccountId?: string; search?: string; limit?: number; offset?: number }) =>
    apiClient.get<any[]>('/petpooja/variations', { params }),
  getAddonGroups: (params?: { restaurantId?: string; businessAccountId?: string; search?: string; groupCategoryId?: string; limit?: number; offset?: number }) =>
    apiClient.get<PetpoojaAddonGroup[]>('/petpooja/addon-groups', { params }),
  getDiscounts: (params?: { restaurantId?: string; businessAccountId?: string }) =>
    apiClient.get<PetpoojaDiscount[]>('/petpooja/discounts', { params }),
  getOrders: (params: { businessAccountId: string; restaurantId?: string; status?: string; search?: string; page?: number; limit?: number }) =>
    apiClient.get<{ data: PetpoojaOrder[]; total: number; page: number; limit: number }>('/petpooja/orders', { params }),
  syncMenu: (payload?: Record<string, any>) => apiClient.post('/petpooja/sync_menu', payload || {}),
  syncToCatalog: (payload: { businessAccountId: string; restaurantId?: string; catalogId?: string; skipExisting?: boolean }) =>
    apiClient.post('/petpooja/sync_to_catalog', payload),
  updateNotificationSettings: (payload: { businessAccountId: string; restaurantId?: string; settings: Record<string, boolean> }) =>
    apiClient.post('/petpooja/notification-settings', payload),
  fetchMenu: (payload?: Record<string, any>) => apiClient.post('/petpooja/fetch_menu', payload || {}),
  saveOrder: (payload?: Record<string, any>) => apiClient.post('/petpooja/save_order', payload || {}),
  updateOrderStatus: (payload: { businessAccountId: string; restID: string; orderID: string; clientorderID: string; status: string; cancelReason?: string }) =>
    apiClient.post('/petpooja/order_status', payload),

  // Item image upload (dashboard "Upload image"). Two steps:
  //   1. presignItemImage: returns a 15-min S3 PUT URL the browser uploads to
  //   2. setItemImage:     persists the resulting CloudFront/S3 URL on the row
  // Pass `imageUrl: null` to setItemImage to clear the image (row falls back
  // to the placeholder until the next PetPooja sync supplies one).
  presignItemImage: (
    itemId: string,
    payload: { fileName: string; fileType: string; fileSize?: number },
  ) =>
    apiClient.post<{
      success: boolean;
      presignedUrl: string;
      fileUrl: string;
      key: string;
      expiresIn: number;
    }>(`/petpooja/items/${encodeURIComponent(itemId)}/image/presign`, payload),

  setItemImage: (itemId: string, payload: { imageUrl: string | null }) =>
    apiClient.put<{ success: boolean; item: { id: string; item_id: string; name: string; image_url: string | null } }>(
      `/petpooja/items/${encodeURIComponent(itemId)}/image`,
      payload,
    ),
};
